<template>
    <div>
        <div class="container-fluid pb-9 mt-7">
            <div class="card">
                <base-header class="pb-3" type="">
                    <div class="row align-items-center py-4">
                        <div class="col-lg-9 col-md-9 col-12">
                            <h6 class="h2 d-inline-block mb-2">Strategy P&L Overview</h6>
                        </div>
                        <div class="col-lg-3 col-md-3 col-6 ">
                            <el-select v-model="selectedYear" placeholder="Select Year" @change="handleYearSelection"
                                class="w-100">
                                <el-option v-for="year in availableYears" :key="year" :label="year.toString()"
                                    :value="year">
                                </el-option>
                            </el-select>
                        </div>
                    </div>
                    <div class="mt-5">
                        <h3> Yearly {{ selectedYear }}</h3>
                        <div class="row align-items-stretch">
                            <div v-for="(strategy, index) in strategies" :key="index"
                                class="col-xl-2 col-md-3 col-xl-card">
                                <div class="card border-0 h-75 px-2 d-flex flex-column justify-content-between">
                                    <div class="text-center card-body">
                                        <h1 class="text-gradient icon-font font-weight-bolder"
                                            :class="getTextColor(strategyTotals[strategy.prop])">
                                            {{ typeof strategyTotals[strategy.prop] === 'number' ?
                                                strategyTotals[strategy.prop].toFixed(2) : "--" }}
                                        </h1>
                                        <h6 class="mb-0 font-weight-bolder">{{ strategyNames[index] }}</h6>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="border-0 card-header pl-0">
                        <h3 class="mb-0">Monthly</h3>
                    </div>
                    <div v-if="selectedYear > currentYear" class="p-4 text-center">
                        <h2 class="text-danger">Expected in the future! No data available for {{ selectedYear }}.</h2>
                    </div>
                    <div v-else>
                        <el-table :data="transformedTableData" class="custom-strategy-table table-responsive" border
                            :fit="true">
                            <el-table-column label="Strategies" prop="strategyName" min-width="140px" align="center">
                                <template #header>
                                    <div class="header-container font-weight-bolder">Strategies</div>
                                </template>
                                <template #default="{ row }">
                                    <div class="header-align strategy-text">{{ row.strategyName }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column v-for="(month, index) in filteredMonths" :key="index"
                                :label="month.substring(0, 3)" min-width="80px" align="center">
                                <template #header>
                                    <div class="header-container font-weight-bolder month-hover">
                                        <el-button type="text" @click="viewDetails(month)" class="icon-btn">
                                            <span>{{ month.substring(0, 3) }}</span>
                                        </el-button>
                                    </div>
                                </template>
                                <template #default="{ row }">
                                    <div class="px-0 header-container"
                                        :class="[getTextColor(row[month]), row[month] >= 0 ? 'positive-number' : '', selectedYear === currentYear ? 'header-align' : '']">
                                        {{ typeof row[month] === 'number' ? row[month].toFixed(2) : "--" }}
                                    </div>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                    <div class="mt-6 mb-3">
                        <h3 class="mb-3">Current week</h3>
                        <div class="row align-items-stretch">
                            <div v-for="(strategy, index) in strategies" :key="index"
                                class="col-xl-2 col-md-3 col-xl-card">
                                <div class="card border-0 h-75 px-2 d-flex flex-column justify-content-between">
                                    <div class="text-center card-body">
                                        <h1 class="text-gradient icon-font font-weight-bolder"
                                            :class="getTextColor(weeklyStrategyTotals[strategy.prop])">
                                            {{ typeof weeklyStrategyTotals[strategy.prop] === 'number' &&
                                                weeklyStrategyTotals[strategy.prop] !== null ?
                                                weeklyStrategyTotals[strategy.prop].toFixed(2) : "--" }}
                                        </h1>
                                        <h6 class="mb-0 font-weight-bolder">{{ strategyNames[index] }}</h6>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </base-header>
            </div>
        </div>
    </div>
</template>
<script>
import { ElTable, ElTableColumn, ElDropdown, ElDropdownMenu, ElSelect, ElOption } from "element-plus";
export default {
    components: {
        [ElTable.name]: ElTable,
        [ElTableColumn.name]: ElTableColumn,
        [ElDropdown.name]: ElDropdown,
        [ElDropdownMenu.name]: ElDropdownMenu,
        [ElSelect.name]: ElSelect,
        [ElOption.name]: ElOption
    },
    data() {
        return {
            availableYears: [],
            selectedYear: null,
            currentYear: new Date().getFullYear(),
            strategyNames: [],
            strategies: [],
            tableData: [],
            strategyTotals: {},
            weeklyStrategyTotals: {},
            fromDate: "",
            toDate: "",
            weekFromDate: "",
            weekToDate: "",
            transformedTableData: [],
            months: [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ],
        };
    },
    created() {
        this.generateYears();
        this.selectedYear = this.currentYear;
        this.setDateRange(this.selectedYear);
    },
    computed: {
        availableYears() {
            const startYear = 2023;
            const endYear = this.currentYear + 7;
            return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
        },
        filteredMonths() {
            return this.selectedYear === this.currentYear
                ? this.months.slice(0, new Date().getMonth() + 1)
                : this.months;
        }
    },
    mounted() {
        this.generateYears();
        this.selectedYear = this.currentYear;
        this.setDateRange(this.selectedYear);
        this.getYearlyData();
        this.getWeeklyData();
        this.getMonthlyData();
        window.scrollTo(0, 0);
    },
    methods: {
        getYearlyData() {
            let data = {
                "fromDate": this.fromDate,
                "toDate": this.toDate
            };
            this.$store.dispatch("strategyDashboard/getStrategyData", data, { root: true })
                .then(response => {
                    const apiData = response.server.data;
                    this.strategies = Object.keys(apiData).map((key, index) => ({
                        prop: `strategy${index + 1}`
                    }));
                    this.strategyNames = Object.keys(apiData).map(key => key.replace(/_/g, ' '));
                    let totals = {}
                    Object.keys(apiData).forEach((key, index) => {
                        totals[`strategy${index + 1}`] = apiData[key] != null ? apiData[key] : null;
                    });
                    this.strategyTotals = totals;
                })
                .catch(error => {
                    console.log(error);
                });
        },
        getWeeklyData() {
            let data = {
                "fromDate": this.weekFromDate,
                "toDate": this.weekToDate
            };
            this.$store.dispatch("strategyDashboard/getStrategyData", data, { root: true })
                .then(response => {
                    const apiData = response.server.data;
                    this.strategies = Object.keys(apiData).map((key, index) => ({
                        prop: `strategy${index + 1}`
                    }));
                    this.strategyNames = Object.keys(apiData).map(key => key.replace(/_/g, ' '));
                    let weeklyTotals = {};
                    Object.keys(apiData).forEach((key, index) => {
                        weeklyTotals[`strategy${index + 1}`] = apiData[key] != null ? apiData[key] : null;
                    });
                    this.weeklyStrategyTotals = weeklyTotals;
                })
                .catch(error => {
                    console.log(error);
                });
        },
        getMonthlyData() {
            this.tableData = {};
            this.transformedTableData = [];
            const today = new Date();
            let lastMonthIndex = 12;
            if (this.selectedYear === today.getFullYear()) {
                lastMonthIndex = today.getMonth() + 1;
            }
            let promises = [];
            for (let i = 0; i < lastMonthIndex; i++) {
                let month = this.months[i];
                let fromDate = `${this.selectedYear}-${(i + 1).toString().padStart(2, "0")}-01`;
                let toDate = `${this.selectedYear}-${(i + 1).toString().padStart(2, "0")}-${new Date(this.selectedYear, i + 1, 0).getDate()}`;
                let data = { "fromDate": fromDate, "toDate": toDate };
                promises.push(
                    this.$store.dispatch("strategyDashboard/getStrategyData", data, { root: true })
                        .then(response => {
                            const monthlyData = response.server.data;
                            if (!this.strategies.length) {
                                this.strategies = Object.keys(monthlyData).map((key, index) => ({
                                    prop: `strategy${index + 1}`
                                }));
                                this.strategyNames = Object.keys(monthlyData).map(key => key.replace(/_/g, ' '));
                            }
                            Object.keys(monthlyData).forEach((key, index) => {
                                let strategyKey = `strategy${index + 1}`;
                                if (!this.tableData[strategyKey]) {
                                    this.tableData[strategyKey] = { strategyName: this.strategyNames[index] };
                                }
                                this.tableData[strategyKey][month] = monthlyData[key] != null ? monthlyData[key] : null;
                            });
                        })
                        .catch(error => {
                            console.log(error);
                        })
                );
            }
            Promise.all(promises).then(() => {
                this.transformedTableData = Object.values(this.tableData);
            });
        },
        generateYears() {
            const startYear = 2023;
            const endYear = this.currentYear + 5;
            this.availableYears = [...Array(endYear - startYear + 1).keys()].map(i => startYear + i);
        },
        handleYearSelection(year) {
            this.selectedYear = year;
            this.setDateRange(year);
            if (this.selectedYear > this.currentYear) {
                this.strategyTotals = {};
                this.strategies.forEach(strategy => {
                    this.strategyTotals[strategy.prop] = "--";
                });
                this.tableData = [];
            } else {
                this.getYearlyData();
                this.getMonthlyData();
            }
        },
        setDateRange(year) {
            this.fromDate = `${year}-01-01`;
            if (year === this.currentYear) {
                const today = new Date();
                this.toDate = today.toISOString().split('T')[0];
            } else {
                this.toDate = `${year}-12-31`;
            }
            const today = new Date();
            const dayOfWeek = today.getDay();
            const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            const weekStart = new Date(today);
            weekStart.setDate(today.getDate() + mondayOffset);
            this.weekFromDate = weekStart.toISOString().split('T')[0];
            this.weekToDate = today.toISOString().split('T')[0];
        },
        getTextColor(value) {
            if (value > 0) {
                return "text-green";
            } else if (value < 0) {
                return "text-red";
            } else {
                return "text-gray";
            }
        },
        viewDetails(month) {
            sessionStorage.setItem("MonthSelected", month);
            sessionStorage.setItem("SelectedYear", this.selectedYear);
            this.$router.push({ path: `/StrategyDetails/${month}` });
        }
    }
};
</script>
